'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@synonymdev/pubky';
import { AuthController } from '@/controllers/auth/auth';
import { isAuthFlowCanceledError } from '@/libs/error/auth-flow-canceled';
import { AuthErrorCode } from '@/libs/error/error.codes';
import { isAppError, isAuthError, isTimeoutError, isWrongEnvironmentHomeserverError } from '@/libs/error/error.utils';
import { Logger } from '@/libs/logger/logger';
import { copyToClipboard } from '@/libs/utils/utils';
import { toast } from '@/molecules/Toaster/toast';
import type { UseAuthUrlOptions, UseAuthUrlReturn } from './useAuthUrl.types';

/** Returns true if the error indicates the auth flow has expired (timeout or SESSION_EXPIRED). */
const UPGRADE_FAILURE_MESSAGE = 'Authorization failed. Approve with the key you are signed in with.';

const isAuthFlowExpiredError = (error: unknown): boolean => {
  if (!isAppError(error)) return false;
  if (isTimeoutError(error)) return true;
  return isAuthError(error) && error.code === AuthErrorCode.SESSION_EXPIRED;
};

/**
 * Manages the authentication URL lifecycle for Pubky Ring authorization.
 * @param options - Configuration for auth URL generation (autoFetch, type, inviteCode for signup)
 * @returns URL state, loading/expired flags, and fetch/copy actions
 */
export function useAuthUrl(options: UseAuthUrlOptions = {}): UseAuthUrlReturn {
  const autoFetch = options.autoFetch ?? true;
  const type = options.type ?? 'signin';
  const inviteCode = options.type === 'signup' ? options.inviteCode : '';

  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(autoFetch);
  const [isExpired, setIsExpired] = useState(false);
  const isMountedRef = useRef(true);
  // Every fetchUrl() call is one request; only the latest request may touch UI state. An older
  // request's late completion or cancellation (StrictMode, refresh, a Passport start superseding
  // this Ring flow) must never overwrite or wipe the URL of a newer one.
  const latestRequestIdRef = useRef(0);

  const fetchUrl = useCallback(async (): Promise<void> => {
    const requestId = ++latestRequestIdRef.current;
    const isCurrentRequest = () => isMountedRef.current && latestRequestIdRef.current === requestId;

    setIsLoading(true);
    setIsExpired(false);
    setUrl('');

    try {
      // Request auth URL from controller
      let result;
      if (type === 'signup') {
        result = await AuthController.getSignupAuthUrl(inviteCode);
      } else if (type === 'upgrade') {
        result = await AuthController.getUpgradeAuthUrl();
      } else {
        result = await AuthController.getAuthUrl();
      }
      const { authorizationUrl, awaitApproval } = result;

      awaitApproval
        .then(async (session: Session) => {
          // No isMountedRef guard here: initializeAuthenticatedSession updates global stores
          // and must run even if the component unmounted (e.g., mobile deeplink handoff where
          // the browser may unmount/remount the page while Pubky Ring is open).
          try {
            if (type === 'upgrade') {
              // A wrong-key approval is reported as `false`, not thrown: the user picked the wrong
              // identity in Pubky Ring, which is a choice to correct, not a fault to report.
              const swapped = await AuthController.upgradeSession({ session });
              if (!swapped) {
                toast({ variant: 'error', description: UPGRADE_FAILURE_MESSAGE });
                if (isMountedRef.current) {
                  setUrl('');
                  setIsExpired(true);
                }
              }
            } else {
              await AuthController.initializeAuthenticatedSession({ session });
            }
          } catch (error) {
            const isWrongEnvironment = isWrongEnvironmentHomeserverError(error);
            if (!isWrongEnvironment && !isAppError(error)) {
              Logger.error('Failed to persist session and check profile:', error);
            }
            const failureMessage = type === 'upgrade' ? UPGRADE_FAILURE_MESSAGE : 'Sign in failed. Try again.';
            toast({
              variant: 'error',
              description: isWrongEnvironment
                ? 'This key is linked to a different homeserver. Use a staging account on this site.'
                : failureMessage,
            });
            if (isCurrentRequest()) {
              setUrl('');
              setIsExpired(true);
            }
          }
        })
        .catch((error: unknown) => {
          if (isAuthFlowCanceledError(error)) {
            // Cancelled by a newer flow (e.g. the user switched to "Continue with Google"): the QR
            // on screen can no longer complete a sign-in, so show the reload state. An older
            // request's cancellation says nothing about the current QR and is ignored.
            if (isCurrentRequest()) {
              setUrl('');
              setIsExpired(true);
            }
            return;
          }

          Logger.error('Authorization promise rejected:', error);
          if (!isCurrentRequest()) return;

          if (isAuthFlowExpiredError(error)) {
            setUrl('');
            setIsExpired(true);
            return;
          }

          toast({
            variant: 'error',
            description: 'Authorization failed. Try again.',
          });
        });

      if (!isCurrentRequest()) return;
      setUrl(authorizationUrl ?? '');
    } catch (error) {
      // A start superseded before it produced a URL (StrictMode double-mount, method switch) is
      // not a failure of the current request; the newer request owns the UI.
      if (isAuthFlowCanceledError(error)) return;

      Logger.error('Failed to generate auth URL:', error);
      if (!isCurrentRequest()) return;
      toast({
        variant: 'error',
        description: 'Could not generate QR. Refresh and try again.',
      });
    } finally {
      if (isCurrentRequest()) {
        setIsLoading(false);
      }
    }
  }, [type, inviteCode]);

  const copyAuthUrl = useCallback(async (): Promise<void> => {
    if (!url) return;
    try {
      await copyToClipboard({ text: url });
    } catch (error) {
      Logger.error('Failed to copy auth URL to clipboard', error);
    }
  }, [url]);

  useEffect(() => {
    isMountedRef.current = true;

    // We intentionally do NOT cancel the auth flow on unmount. The polling must survive
    // component lifecycle changes (e.g., mobile deeplink handoff where the browser may
    // unmount/remount the component while the user is in Pubky Ring). The AuthController
    // already self-manages flow lifetime: it cancels stale flows when a new one starts
    // (getAuthUrl), on successful session init, and on sign-out.
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!autoFetch) return;
    void fetchUrl();
  }, [autoFetch, fetchUrl]);

  return {
    url,
    isLoading,
    isExpired,
    fetchUrl,
    copyAuthUrl,
  };
}
